import { Opportunity as OpportunityEntity, type Opportunity, type PartnerOrganization } from "@entities";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import TableChartIcon from "@mui/icons-material/TableChart";
import {
  Avatar,
  Box,
  Button,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { heIL } from "@mui/x-date-pickers/locales";
import { opportunityRepo, partnerOrganizationRepo } from "@repositories";
import { CommunicationRecordManager } from "../../../components/CommunicationRecordManager/CommunicationRecordManager";
import { CommunicationRecordsDialog } from "../../../components/CommunicationRecordManager/CommunicationRecordsDialog";
import PhoneNumberCell from "../PhoneNumberCell";
import { formatOpportunityStartDisplay } from "@shared/helpers/format-opportunity-schedule";
import { sameOriginImageURL } from "@shared/helpers/constants";
import {
  getPartnerOrgOpportunityListStatus,
  opportunityMissingSeats,
  partnerOrgLeafOpportunitiesWhereForChip,
  type PartnerOrgLeafChip,
} from "@shared/helpers/partner-org-opportunity-status";
import { partnerOrgNeedsContact } from "@shared/helpers/partner-organization-contact";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAlert } from "../../../contexts/AlertProvider";
import dayjs, { type Dayjs } from "dayjs";
import "dayjs/locale/he";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  cooperationLevelChipColor,
  cooperationLevelShortLabel,
} from "./cooperation-level";
import { partnerOrgHandledByLabel } from "@shared/helpers/partner-organization-handled-by";
import {
  fetchPartnerOrganizationCatalogStats,
  formatPartnerOrgActivityDate,
  formatPartnerOrgFollowUpDate,
} from "./partner-organization-activity";
import { applyPartnerOrgFilterToOpportunitiesTable } from "./partner-organization-opportunities-nav";
import type { PartnerOrganizationCoverageRow } from "./partnerOrganizationCoverageApi";

type Props = {
  open: boolean;
  organization: PartnerOrganization | null;
  coverageRow?: PartnerOrganizationCoverageRow | null;
  registrationDeadlineFrom?: string | null;
  onClose: () => void;
  onEdit?: (organization: PartnerOrganization) => void;
  onOpenOrganization: (organization: PartnerOrganization) => void;
  onAddChild?: (parent: PartnerOrganization) => void;
};

const LEAF_PAGE_SIZE = 3;

function HierarchyPanel({
  organization,
  childOrganizations,
  childrenLoading,
  onOpenOrganization,
  onAddChild,
}: {
  organization: PartnerOrganization;
  childOrganizations: PartnerOrganization[];
  childrenLoading: boolean;
  onOpenOrganization: (organization: PartnerOrganization) => void;
  onAddChild?: (parent: PartnerOrganization) => void;
}) {
  const parentKnown = Object.prototype.hasOwnProperty.call(organization, "parent");
  const parent = organization.parent ?? null;
  const atMaxDepth = Boolean(parent?.parentId);
  return (
    <Stack
      gap={0.5}
      sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}
    >
      <Typography variant="subtitle2">היררכיה</Typography>
      <Typography variant="caption" color="text.secondary">
        רמה אחת למעלה ולמטה. המשימות למטה שייכות לארגון הזה.
      </Typography>
      {parentKnown && parent ? (
        <List disablePadding>
          <ListItemButton
            onClick={() => onOpenOrganization(parent)}
            sx={{ borderRadius: 1, px: 1 }}
          >
            <ListItemText
              primary={parent.name}
              secondary="ארגון אב"
              primaryTypographyProps={{ fontWeight: 650 }}
              secondaryTypographyProps={{ variant: "caption" }}
            />
          </ListItemButton>
        </List>
      ) : parentKnown ? (
        <Typography variant="body2" color="text.secondary">
          ארגון ראשי
        </Typography>
      ) : null}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
        תתי ארגונים
      </Typography>
      {childrenLoading ? null : childOrganizations.length > 0 ? (
        <List disablePadding>
          {childOrganizations.map((child) => (
            <ListItemButton
              key={child.id}
              onClick={() => onOpenOrganization(child)}
              sx={{ borderRadius: 1, px: 1 }}
            >
              <ListItemText
                primary={child.name}
                primaryTypographyProps={{ fontWeight: 650 }}
              />
            </ListItemButton>
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary">
          אין תתי ארגונים
        </Typography>
      )}
      {onAddChild && parentKnown && !atMaxDepth ? (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => onAddChild(organization)}
          sx={{ alignSelf: "flex-start", mt: 0.5, textTransform: "none" }}
        >
          תת ארגון חדש
        </Button>
      ) : null}
      {atMaxDepth ? (
        <Typography variant="caption" color="text.secondary">
          זו הרמה השלישית. אי אפשר להוסיף עוד תת ארגון.
        </Typography>
      ) : null}
    </Stack>
  );
}

const LEAF_CHIPS: { id: PartnerOrgLeafChip; label: string }[] = [
  { id: "open", label: "פתוחים" },
  { id: "empty", label: "ריקים" },
  { id: "soon", label: "קרובים 7 ימים" },
  { id: "aid", label: "בקשות סיוע" },
];

function leafStatusChipColor(
  status: string,
  missing: number,
  participants: number,
): "default" | "primary" | "secondary" | "error" {
  if (status === "בקשת סיוע") return "secondary";
  if (status === "מלאה") return "default";
  if (participants === 0 || missing > 0) return "error";
  return "primary";
}

export const PartnerOrganizationWorkDrawer: FC<Props> = ({
  open,
  organization,
  coverageRow,
  registrationDeadlineFrom,
  onClose,
  onEdit,
  onOpenOrganization,
  onAddChild,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setAlertText, setSeverity, setTime } = useAlert();
  const [leafChip, setLeafChip] = useState<PartnerOrgLeafChip>("open");
  const [drawerTab, setDrawerTab] = useState<"leaves" | "comm">("leaves");
  const [didAutoSwitchTab, setDidAutoSwitchTab] = useState(false);
  const [commDialogOpen, setCommDialogOpen] = useState(false);
  const [commTick, setCommTick] = useState(0);
  const [followUp, setFollowUp] = useState<Dayjs | null>(null);
  const [followUpSaving, setFollowUpSaving] = useState(false);
  const [commentsDraft, setCommentsDraft] = useState("");
  const [commentsSaving, setCommentsSaving] = useState(false);

  useEffect(() => {
    setLeafChip("open");
    setDrawerTab("leaves");
    setDidAutoSwitchTab(false);
    setCommDialogOpen(false);
    setCommTick(0);
  }, [organization?.id]);

  useEffect(() => {
    const raw =
      organization?.nextFollowUpAt ?? coverageRow?.nextFollowUpAt ?? null;
    setFollowUp(raw ? dayjs(raw) : null);
  }, [
    organization?.id,
    organization?.nextFollowUpAt,
    coverageRow?.nextFollowUpAt,
  ]);

  useEffect(() => {
    setCommentsDraft(
      organization?.comments ?? coverageRow?.comments ?? "",
    );
  }, [organization?.id, organization?.comments, coverageRow?.comments]);

  const {
    data: leavesPages,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: [
      "partner-org-leaves",
      organization?.id ?? "",
      leafChip,
      registrationDeadlineFrom ?? "",
    ],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const chipWhere = partnerOrgLeafOpportunitiesWhereForChip(
        organization!.id,
        leafChip,
      );
      return opportunityRepo.find({
        where: registrationDeadlineFrom
          ? {
              $and: [
                chipWhere,
                OpportunityEntity.registrationDeadlineOnOrAfter({
                  dateFrom: registrationDeadlineFrom,
                }),
              ],
            }
          : chipWhere,
        include: {
          parentOpportunity: true,
        },
        orderBy: { startDate: "asc" },
        limit: LEAF_PAGE_SIZE,
        page: pageParam,
      });
    },
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.length === LEAF_PAGE_SIZE ? lastPageParam + 1 : undefined,
    enabled: open && !!organization?.id,
    staleTime: 15_000,
  });

  const visibleLeaves = leavesPages?.pages.flat() ?? [];

  const {
    data: catalog,
    isLoading: isCatalogLoading,
    isSuccess: isCatalogSuccess,
  } = useQuery({
    queryKey: [
      "partner-org-catalog-stats",
      organization?.id ?? "",
      commTick,
    ],
    queryFn: () => fetchPartnerOrganizationCatalogStats([organization!.id]),
    enabled: open && !!organization?.id,
    staleTime: 15_000,
  });
  const openLeavesCount = organization?.id
    ? catalog?.openLeavesCount[organization.id] ?? 0
    : 0;

  const loadMoreLockRef = useRef(false);

  const loadMoreLeaves = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage || loadMoreLockRef.current) return;
    loadMoreLockRef.current = true;
    void fetchNextPage().finally(() => {
      loadMoreLockRef.current = false;
    });
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const activity = organization?.id
    ? catalog?.activity[organization.id]
    : undefined;

  useEffect(() => {
    if (!open || isCatalogLoading || didAutoSwitchTab) return;
    if (isCatalogSuccess && openLeavesCount === 0) {
      setDrawerTab("comm");
    }
    setDidAutoSwitchTab(true);
  }, [
    open,
    isCatalogLoading,
    isCatalogSuccess,
    openLeavesCount,
    didAutoSwitchTab,
  ]);

  const { data: childOrganizations = [], isLoading: childrenLoading } = useQuery({
    queryKey: ["partner-org-children", organization?.id ?? ""],
    queryFn: () =>
      partnerOrganizationRepo.find({
        where: { parentId: organization!.id },
        include: { parent: true },
        orderBy: { name: "asc" },
      }),
    enabled: open && !!organization?.id,
  });

  const logoId = (organization?.logoImageId || "").trim();
  const cooperationLabel = cooperationLevelShortLabel(
    organization?.cooperationLevel,
  );
  const handledByLabel = partnerOrgHandledByLabel(organization?.handledBy);
  const responsibleName =
    organization?.handlingManager?.name?.trim() ||
    coverageRow?.handlingManagerName?.trim() ||
    "";
  const missingSeats = coverageRow?.missingSeats;
  const needsContact = partnerOrgNeedsContact({
    nextFollowUpAt: followUp?.toDate() ?? organization?.nextFollowUpAt,
    lastCommunicationAt:
      coverageRow?.lastCommunicationAt ?? activity?.lastCommunicationAt,
    cooperationLevel: organization?.cooperationLevel,
  });
  const contactManagers = (organization?.managers ?? [])
    .filter((manager) => !!manager.phoneNumber)
    .slice(0, 2);
  const addressLine =
    organization?.addresses
      ?.slice()
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((row) => row.address)
      .filter(Boolean)
      .slice(0, 2)
      .join(" · ") ||
    organization?.address ||
    "";

  const saveFollowUp = async (value: Dayjs | null) => {
    if (!organization || !onEdit) return;
    const next = value?.isValid() ? value.startOf("day").toDate() : null;
    setFollowUp(value?.isValid() ? value.startOf("day") : null);
    setFollowUpSaving(true);
    try {
      await partnerOrganizationRepo.update(organization.id, {
        nextFollowUpAt: next,
      });
      organization.nextFollowUpAt = next;
      if (coverageRow) coverageRow.nextFollowUpAt = next;
      await queryClient.invalidateQueries({
        queryKey: ["partner-organization-coverage"],
      });
    } catch {
      setFollowUp(
        organization.nextFollowUpAt
          ? dayjs(organization.nextFollowUpAt)
          : null,
      );
      setSeverity("error");
      setAlertText("לא הצלחנו לשמור את תאריך החזרה");
      setTime(4000);
    } finally {
      setFollowUpSaving(false);
    }
  };

  const saveComments = async () => {
    if (!organization) return;
    const next = commentsDraft.trim();
    setCommentsSaving(true);
    try {
      await partnerOrganizationRepo.update(organization.id, {
        comments: next,
      });
      organization.comments = next;
      if (coverageRow) coverageRow.comments = next;
      setCommentsDraft(next);
      await queryClient.invalidateQueries({
        queryKey: ["partner-organization-coverage"],
      });
    } catch {
      setCommentsDraft(organization.comments ?? coverageRow?.comments ?? "");
      setSeverity("error");
      setAlertText("לא הצלחנו לשמור את ההערות");
      setTime(4000);
    } finally {
      setCommentsSaving(false);
    }
  };

  const openInTable = () => {
    if (!organization) return;
    applyPartnerOrgFilterToOpportunitiesTable(
      organization.id,
      organization.name,
    );
    onClose();
    navigate("/events");
  };

  const openMission = (opportunityId: string) => {
    onClose();
    navigate(`/events/${opportunityId}`);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 440 },
          maxWidth: "100%",
        },
      }}
    >
      {!organization ? null : (
        <Stack sx={{ height: "100%" }} dir="rtl">
          <Stack
            direction="row"
            alignItems="flex-start"
            gap={1.5}
            sx={{ p: 2, pb: 1.5, borderBottom: 1, borderColor: "divider" }}
          >
            <Avatar
              src={logoId ? sameOriginImageURL(logoId) : undefined}
              alt={organization.name}
              imgProps={{ referrerPolicy: "no-referrer" }}
              sx={{ width: 48, height: 48 }}
            />
            <Stack sx={{ minWidth: 0, flex: 1 }} gap={0.5}>
              <Typography variant="h6" fontWeight={700} noWrap>
                {organization.name}
              </Typography>
              {addressLine ? (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {addressLine}
                </Typography>
              ) : null}
              <Stack direction="row" gap={0.75} flexWrap="wrap">
                {handledByLabel ? (
                  <Chip
                    size="small"
                    label={handledByLabel}
                    color={
                      organization.handledBy === "CALL_CENTER"
                        ? "primary"
                        : "secondary"
                    }
                  />
                ) : null}
                {responsibleName ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`אחראי: ${responsibleName}`}
                  />
                ) : null}
                {cooperationLabel ? (
                  <Chip
                    size="small"
                    label={cooperationLabel}
                    color={cooperationLevelChipColor(
                      organization.cooperationLevel,
                    )}
                  />
                ) : null}
                {missingSeats != null && missingSeats > 0 ? (
                  <Chip
                    size="small"
                    color="error"
                    variant="outlined"
                    label={`חוסר ${missingSeats}`}
                  />
                ) : null}
                {needsContact ? (
                  <Chip
                    size="small"
                    color="error"
                    variant="outlined"
                    label="דורש מגע"
                  />
                ) : null}
              </Stack>
              {contactManagers.length > 0 ? (
                <Stack gap={0.5}>
                  {contactManagers.map((manager) => (
                    <Stack
                      key={manager.id}
                      direction="row"
                      alignItems="center"
                      gap={1}
                      flexWrap="wrap"
                    >
                      <Typography variant="caption" color="text.secondary">
                        {manager.name || "מנהל אפליקציה"}
                        {manager.partnerContactRole
                          ? ` · ${manager.partnerContactRole}`
                          : ""}
                        {manager.email ? ` · ${manager.email}` : ""}
                      </Typography>
                      <PhoneNumberCell phoneNumber={manager.phoneNumber} />
                    </Stack>
                  ))}
                </Stack>
              ) : null}
              <Typography variant="caption" color="text.secondary">
                כניסה אחרונה: {formatPartnerOrgActivityDate(activity?.lastLoginAt)}
                {" · "}
                צורך אחרון: {formatPartnerOrgActivityDate(activity?.lastNeedAt)}
                {" · "}
                תקשורת: {formatPartnerOrgActivityDate(
                  coverageRow?.lastCommunicationAt ??
                    activity?.lastCommunicationAt,
                )}
              </Typography>
              {onEdit ? (
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="he"
                  localeText={
                    heIL.components.MuiLocalizationProvider.defaultProps
                      .localeText
                  }
                >
                  <DatePicker
                    label="לחזור בתאריך"
                    value={followUp}
                    disabled={followUpSaving}
                    onChange={(value) => {
                      void saveFollowUp(value);
                    }}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: {
                        size: "small",
                        helperText: followUpSaving
                          ? "שומר..."
                          : "אם הגיע התאריך - דורש מגע",
                      },
                      field: { clearable: true },
                      layout: {
                        sx: {
                          "& .MuiMonthCalendar-root": { direction: "ltr" },
                          "& .MuiYearCalendar-root": { direction: "ltr" },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  חזרה:{" "}
                  {formatPartnerOrgFollowUpDate(
                    followUp?.toDate() ??
                      organization.nextFollowUpAt ??
                      coverageRow?.nextFollowUpAt,
                  )}
                </Typography>
              )}
              <TextField
                label="הערות פנימיות"
                value={commentsDraft}
                onChange={(e) => setCommentsDraft(e.target.value)}
                multiline
                minRows={2}
                maxRows={4}
                size="small"
                fullWidth
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => void saveComments()}
                disabled={commentsSaving}
                sx={{ alignSelf: "flex-start", textTransform: "none" }}
              >
                {commentsSaving ? "שומר..." : "שמירת הערות"}
              </Button>
            </Stack>
            <IconButton onClick={onClose} aria-label="סגירה" size="small">
              <CloseIcon />
            </IconButton>
          </Stack>

          <HierarchyPanel
            organization={organization}
            childOrganizations={childOrganizations}
            childrenLoading={childrenLoading && childOrganizations.length === 0}
            onOpenOrganization={onOpenOrganization}
            onAddChild={onAddChild}
          />

          <Stack sx={{ p: 2, gap: 1.5, flex: 1, minHeight: 0 }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={drawerTab}
              onChange={(_, value: "leaves" | "comm" | null) => {
                if (value) setDrawerTab(value);
              }}
              sx={{
                "& .MuiToggleButton-root": {
                  borderRadius: "1rem",
                  textTransform: "none",
                  px: 1.5,
                },
              }}
            >
              <ToggleButton value="leaves">מופעים</ToggleButton>
              <ToggleButton value="comm">תקשורת</ToggleButton>
            </ToggleButtonGroup>

            {drawerTab === "comm" ? (
              <>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                >
                  <Typography variant="subtitle2" color="text.secondary">
                    תיעוד תקשורת
                  </Typography>
                  <Button size="small" onClick={() => setCommDialogOpen(true)}>
                    הצג הכל
                  </Button>
                </Stack>
                <Box
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: "12px",
                    flex: 1,
                    minHeight: 0,
                    overflow: "auto",
                  }}
                >
                  <CommunicationRecordManager
                    key={`${organization.id}-${commTick}`}
                    partnerOrganizationId={organization.id}
                    type="partner-organization"
                    showAllRecords
                    embedded
                    onSave={() => {
                      setCommTick((n) => n + 1);
                      void queryClient.invalidateQueries({
                        queryKey: ["partner-organization-coverage"],
                      });
                    }}
                  />
                </Box>
              </>
            ) : (
              <>
            <Typography variant="subtitle2" color="text.secondary">
              מופעים מתועדפים
            </Typography>
            <Stack direction="row" gap={0.75} flexWrap="wrap">
              {LEAF_CHIPS.map((chip) => (
                <Chip
                  key={chip.id}
                  size="small"
                  label={chip.label}
                  color={leafChip === chip.id ? "secondary" : "default"}
                  variant={leafChip === chip.id ? "filled" : "outlined"}
                  onClick={() => setLeafChip(chip.id)}
                />
              ))}
            </Stack>

            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
              {isLoading ? (
                <Stack alignItems="center" py={6}>
                  <CircularProgress size={32} />
                </Stack>
              ) : visibleLeaves.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  {leafChip === "open"
                    ? "אין מופעים פתוחים לארגון זה."
                    : "אין מופעים לפי הסינון הזה."}
                </Typography>
              ) : (
                <Stack gap={1}>
                  {visibleLeaves.map((opportunity: Opportunity) => {
                    const status =
                      getPartnerOrgOpportunityListStatus(opportunity);
                    const missing = opportunityMissingSeats(opportunity);
                    const participants = opportunity.participantsCount ?? 0;
                    const when = formatOpportunityStartDisplay(opportunity);
                    const groupTitle = opportunity.parentOpportunity?.title;
                    return (
                      <Box
                        key={opportunity.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => openMission(opportunity.id)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            openMission(opportunity.id);
                          }
                        }}
                        sx={{
                          p: 1.5,
                          borderRadius: "12px",
                          border: 1,
                          borderColor: "divider",
                          cursor: "pointer",
                          "&:hover": { borderColor: "secondary.main" },
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          gap={1}
                          alignItems="flex-start"
                        >
                          <Typography variant="body2" color="text.secondary">
                            {when}
                          </Typography>
                          <Chip
                            size="small"
                            label={
                              participants === 0 && status === "פתוחה"
                                ? "ריק"
                                : status
                            }
                            color={leafStatusChipColor(
                              status,
                              missing,
                              participants,
                            )}
                            variant="outlined"
                          />
                        </Stack>
                        <Typography fontWeight={600} sx={{ mt: 0.5 }}>
                          {opportunity.title || "ללא כותרת"}
                        </Typography>
                        {groupTitle ? (
                          <Typography variant="caption" color="text.secondary">
                            {groupTitle}
                          </Typography>
                        ) : null}
                        {opportunity.location ? (
                          <Typography variant="body2" color="text.secondary">
                            {opportunity.location}
                          </Typography>
                        ) : null}
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          sx={{ mt: 0.75 }}
                        >
                          <Typography variant="body2">
                            {participants} מתוך {opportunity.amount ?? 0}
                          </Typography>
                          {missing > 0 ? (
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              color="error.main"
                            >
                              חסר {missing}
                            </Typography>
                          ) : null}
                        </Stack>
                      </Box>
                    );
                  })}
                  {hasNextPage ? (
                    <Stack alignItems="center" sx={{ py: 0.5 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={isFetchingNextPage}
                        onClick={() => loadMoreLeaves()}
                        startIcon={
                          isFetchingNextPage ? (
                            <CircularProgress size={14} />
                          ) : undefined
                        }
                        sx={{ textTransform: "none" }}
                      >
                        {isFetchingNextPage ? "טוען..." : "טען עוד"}
                      </Button>
                    </Stack>
                  ) : null}
                </Stack>
              )}
            </Box>
              </>
            )}
            <CommunicationRecordsDialog
              open={commDialogOpen}
              onClose={() => setCommDialogOpen(false)}
              title={organization.name}
              partnerOrganizationId={organization.id}
              type="partner-organization"
              onSave={() => {
                setCommTick((n) => n + 1);
                void queryClient.invalidateQueries({
                  queryKey: ["partner-organization-coverage"],
                });
              }}
            />
          </Stack>

          <Stack
            direction="row"
            gap={1}
            flexWrap="wrap"
            sx={{ p: 2, borderTop: 1, borderColor: "divider" }}
          >
            {onEdit ? (
              <Button
                startIcon={<EditIcon />}
                onClick={() => {
                  onClose();
                  onEdit(organization);
                }}
              >
                עריכת ארגון
              </Button>
            ) : null}
            <Button
              variant="outlined"
              startIcon={<TableChartIcon />}
              onClick={openInTable}
            >
              טבלת משימות
            </Button>
          </Stack>
        </Stack>
      )}
    </Drawer>
  );
};
